import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth API
export const authAPI = {
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (data) => axios.post(`${API}/auth/register`, data),
  getMe: () => axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),
  logout: () => axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeader() }),
};

// Projects API
export const projectsAPI = {
  list: (params) => axios.get(`${API}/projects`, { headers: getAuthHeader(), params }),
  get: (id) => axios.get(`${API}/projects/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/projects`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/projects/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/projects/${id}`, { headers: getAuthHeader() }),
  approve: (projectId, verificationMode) => 
    axios.put(`${API}/projects/${projectId}/approve?verification_mode=${verificationMode || 'single'}`, {}, { headers: getAuthHeader() }),
  reject: (projectId, reason) => 
    axios.put(`${API}/projects/${projectId}/reject?reason=${encodeURIComponent(reason || '')}`, {}, { headers: getAuthHeader() }),
  assignAgent: (projectId, agentId) => 
    axios.put(`${API}/projects/${projectId}/assign-agent?agent_id=${agentId}`, {}, { headers: getAuthHeader() }),
  assignAgents: (projectId, agentId1, agentId2) =>
    axios.put(`${API}/projects/${projectId}/assign-agents?agent_id_1=${agentId1}&agent_id_2=${agentId2}`, {}, { headers: getAuthHeader() }),
  setVerificationMode: (projectId, mode, threshold) =>
    axios.put(`${API}/projects/${projectId}/set-verification-mode?mode=${mode}&threshold=${threshold || 10}`, {}, { headers: getAuthHeader() }),
  startVerification: (projectId) => 
    axios.put(`${API}/projects/${projectId}/start-verification`, {}, { headers: getAuthHeader() }),
  updateProgress: (projectId, progress) => 
    axios.put(`${API}/projects/${projectId}/progress?progress=${progress}`, {}, { headers: getAuthHeader() }),
  getIndicatorComparison: (projectId) =>
    axios.get(`${API}/projects/${projectId}/indicator-comparison`, { headers: getAuthHeader() }),
};

// Reports API
export const reportsAPI = {
  list: (params) => axios.get(`${API}/reports`, { headers: getAuthHeader(), params }),
  get: (id) => axios.get(`${API}/reports/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/reports`, data, { headers: getAuthHeader() }),
  submit: (reportId) => axios.put(`${API}/reports/${reportId}/submit`, {}, { headers: getAuthHeader() }),
  review: (reportId, action, feedback) => 
    axios.put(`${API}/admin/review-report/${reportId}?action=${action}&feedback=${encodeURIComponent(feedback || '')}`, {}, { headers: getAuthHeader() }),
  donorFeedback: (reportId, action, comments) =>
    axios.put(`${API}/reports/${reportId}/donor-feedback`, { action, comments }, { headers: getAuthHeader() }),
  verify: (id, status) => axios.put(`${API}/reports/${id}/verify?status=${status}`, {}, { headers: getAuthHeader() }),
};

// Admin API
export const adminAPI = {
  approveReport: (reportId, action, feedback) =>
    axios.put(`${API}/admin/review-report/${reportId}?action=${action}&feedback=${encodeURIComponent(feedback || '')}`, {}, { headers: getAuthHeader() }),
  approveBoth: (projectId, feedback) =>
    axios.put(`${API}/admin/approve-both/${projectId}?feedback=${encodeURIComponent(feedback || '')}`, {}, { headers: getAuthHeader() }),
  releaseProjectReports: (projectId) =>
    axios.put(`${API}/admin/release-project-reports/${projectId}`, {}, { headers: getAuthHeader() }),
  setFinalValues: (projectId, data) =>
    axios.put(`${API}/admin/final-values/${projectId}`, data, { headers: getAuthHeader() }),
  requestRevisionAgent: (projectId, agentId, feedback) =>
    axios.put(`${API}/admin/request-revision-agent/${projectId}?agent_id=${agentId}&feedback=${encodeURIComponent(feedback || '')}`, {}, { headers: getAuthHeader() }),
};

// Users API
export const usersAPI = {
  list: () => axios.get(`${API}/users`, { headers: getAuthHeader() }),
  listPending: () => axios.get(`${API}/users/pending`, { headers: getAuthHeader() }),
  listAgents: (params) => axios.get(`${API}/users/agents`, { headers: getAuthHeader(), params }),
  listDonors: () => axios.get(`${API}/users/donors`, { headers: getAuthHeader() }),
  approve: (userId) => axios.put(`${API}/users/${userId}/approve`, {}, { headers: getAuthHeader() }),
  reject: (userId) => axios.put(`${API}/users/${userId}/reject`, {}, { headers: getAuthHeader() }),
  updateRole: (userId, role) => axios.put(`${API}/users/${userId}/role?role=${role}`, {}, { headers: getAuthHeader() }),
  updateStatus: (userId, isActive) => axios.put(`${API}/users/${userId}/status?is_active=${isActive}`, {}, { headers: getAuthHeader() }),
};

// Organizations API
export const organizationsAPI = {
  list: () => axios.get(`${API}/organizations`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/organizations`, data, { headers: getAuthHeader() }),
};

// Analytics API
export const analyticsAPI = {
  overview: () => axios.get(`${API}/analytics/overview`, { headers: getAuthHeader() }),
  project: (projectId) => axios.get(`${API}/analytics/projects/${projectId}`, { headers: getAuthHeader() }),
  chartData: () => axios.get(`${API}/analytics/chart-data`, { headers: getAuthHeader() }),
};

// Surveys API
export const surveysAPI = {
  list: (params) => axios.get(`${API}/surveys`, { headers: getAuthHeader(), params }),
  create: (data) => axios.post(`${API}/surveys`, data, { headers: getAuthHeader() }),
  submit: (data) => axios.post(`${API}/surveys/submit`, data, { headers: getAuthHeader() }),
  listSubmissions: (params) => axios.get(`${API}/surveys/submissions`, { headers: getAuthHeader(), params }),
};

// Files API
export const filesAPI = {
  upload: (file, projectId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) formData.append('project_id', projectId);
    return axios.post(`${API}/files/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fileId) => axios.get(`${API}/files/${fileId}`, { 
    headers: getAuthHeader(),
    responseType: 'blob'
  }),
  view: (fileId) => {
    const token = localStorage.getItem('token');
    return `${API}/files/${fileId}/view?auth=${token}`;
  },
  delete: (fileId) => axios.delete(`${API}/files/${fileId}`, { headers: getAuthHeader() }),
};

// Export API
export const exportAPI = {
  projectReport: (projectId) => axios.get(`${API}/export/project/${projectId}`, { 
    headers: getAuthHeader(),
    responseType: 'blob'
  }),
};

export default {
  auth: authAPI,
  projects: projectsAPI,
  reports: reportsAPI,
  users: usersAPI,
  organizations: organizationsAPI,
  analytics: analyticsAPI,
  surveys: surveysAPI,
  files: filesAPI,
  export: exportAPI,
};
